import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type JobEditorState =
  | { mode: 'new'; date: string; time?: string }
  /** techMode: opened from a run sheet or the technician dashboard to complete the job in the field. */
  | { mode: 'edit'; jobId: string; techMode?: boolean; returnTo?: { tech: string; date: string } };

export interface UIState {
  jobEditor: JobEditorState | null;
  compliance: { jobId: string; type: 'post' | 'armp' | 'termite' } | null;
  daySchedule: { date: string } | null;
  runSheet: { tech: string; date: string } | null;
  armpReview: { jobKey: string } | null;
  recordPreview: { recordId: string } | null;
  recordDetails: { recordId: string } | null;
  photo: { src: string; name: string } | null;
  /** Rodent plan to load in the planner, set by "Open plan" anywhere in the app. */
  rodentPlanRequest: { planId: string; at: number } | null;
}

const empty: UIState = { jobEditor: null, compliance: null, daySchedule: null, runSheet: null, armpReview: null, recordPreview: null, recordDetails: null, photo: null, rodentPlanRequest: null };

interface UIValue extends UIState {
  open<K extends keyof UIState>(key: K, value: NonNullable<UIState[K]>): void;
  close(key: keyof UIState): void;
}

const UIContext = createContext<UIValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UIState>(empty);
  const value = useMemo<UIValue>(() => ({
    ...state,
    open: (key, next) => setState((current) => ({ ...current, [key]: next })),
    close: (key) => setState((current) => ({ ...current, [key]: null }))
  }), [state]);
  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI(): UIValue {
  const value = useContext(UIContext);
  if (!value) throw new Error('useUI must be used inside UIProvider');
  return value;
}
